import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { Feature, GeoData } from '../../../models/geo-data';
import { MenuItem } from 'primeng/api';
import { getState, patchState } from '@ngrx/signals';
import { ButtonModule } from 'primeng/button';
import { NgForOf, NgIf } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { AlertStyleDirective } from '../../../directives/alert-style.directive';
import { HeaderComponent } from '../../atoms/header/header.component';
import { DivOverlay, GeoJSON } from 'leaflet';
import { DataLayer } from '../../../models/data-layer';
import { GlobalsStore } from '../../../stores/globals.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SearchInputComponent } from '../../atoms/search-input/search-input.component';
import { PanelModule } from 'primeng/panel';
import { Menu, MenuModule } from 'primeng/menu';
import { FeatureMetaFormComponent } from '../feature-meta-form/feature-meta-form.component';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { AppUtilityService } from '../../../utility/app.utility';
import { ToastModule } from 'primeng/toast';
import { PaginatorModule } from 'primeng/paginator';
import { DataViewModule } from 'primeng/dataview';
import { YearListComponent } from '../year-list/year-list.component';
import { CheckboxModule } from 'primeng/checkbox';
import { PermissionService } from '../../../services/permission.service';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';

import { GeoGroupDropdownComponent } from '../../atoms/geo-group-dropdown/geo-group-dropdown.component';

@Component({
  selector: 'app-feature-form',
  standalone: true,
  imports: [
    ButtonModule,
    ReactiveFormsModule,
    NgForOf,
    InputTextModule,
    DividerModule,
    AlertStyleDirective,
    HeaderComponent,
    NgIf,
    IconFieldModule,
    InputIconModule,
    SearchInputComponent,
    PanelModule,
    MenuModule,
    FeatureMetaFormComponent,
    AlertComponent,
    ToastModule,
    PaginatorModule,
    DataViewModule,
    YearListComponent,
    CheckboxModule,
    TableModule,
    TooltipModule,
    DialogModule,
    TableModule,
    GeoGroupDropdownComponent,
  ],
  templateUrl: './feature-form.component.html',
  styleUrl: './feature-form.component.scss',
})
export class FeatureFormComponent implements OnChanges, OnInit, OnDestroy {
  featureForm: FormGroup = new FormGroup({});
  readonly geoDataStore = inject(GeoDataStore);
  readonly globalStore = inject(GlobalsStore);
  readonly layerStore = inject(DataLayerStore);
  @Output() submitted: EventEmitter<void> = new EventEmitter<void>();
  @Output() cancelled: EventEmitter<void> = new EventEmitter<void>();
  @Output() triggerConvert: EventEmitter<void> = new EventEmitter<void>();
  @Input() type: 'Point' | 'Polygon' = 'Polygon';
  @Input() activeImport = false;
  @Input() convertAvailable = false;
  @Input() selectedLayer: DataLayer | null = null;
  @Input() decoupleFromSiblings = false;
  @Input() inputFeatures?: GeoJSON.Feature<
    GeoJSON.Point | GeoJSON.Polygon,
    GeoJSON.GeoJsonProperties
  >[];

  initialized = false;
  currentFeatures: GeoJSON.Feature<
    GeoJSON.Point | GeoJSON.Polygon,
    GeoJSON.GeoJsonProperties
  >[] = this.inputFeatures || [];
  selectedGeoData: GeoData | null = null;
  filteredFeatures: GeoJSON.Feature<
    GeoJSON.Point | GeoJSON.Polygon,
    GeoJSON.GeoJsonProperties
  >[] = [];
  items: MenuItem[] = [];
  featureToEdit?: FormGroup;
  layerSiblings: DataLayer[] = [];
  featureToEditIndex?: number;
  sharedGeoDataLayers: DataLayer[] = [];
  overrideLayersByDifferences = false;
  editedIndices: Set<number> = new Set<number>();
  featureEdited = false;
  groups: string[] = [];
  defaultGroup = '';
  public ignoreDifferentKeysValidator = false;
  public isReadonly = false;
  public first = 0;
  public rows = 10;

  constructor(
    private fb: FormBuilder,
    private appUtilityService: AppUtilityService,
    private permissionService: PermissionService
  ) {
    // store effects
    effect(() => {
      const geoState = getState(this.geoDataStore);
      const globalState = getState(this.globalStore);
      const layerState = getState(this.layerStore);

      this.selectedGeoData = this.geoDataStore.selectedItem();
      this.checkSharedLayers(layerState.selection);

      if (geoState.initialized && layerState.initialized) this.renderForm();
    });
  }

  ngOnInit() {
    this.init();
  }

  /**
   * Responds to changes to the input properties of the component.
   * This method is called whenever Angular detects input property changes.
   *
   * @param {SimpleChanges} changes - Object of changes. Each changed property is represented as a key,
   *                                   and its value contains metadata about the change,
   *                                   including previous and current values.
   * @return {void} This method does not return any value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['inputFeatures'] ||
      changes['selectedLayer'] ||
      changes['activeImport']
    ) {
      this.init();
    }
  }

  /**
   * Initializes the component by managing layer selection, sibling layers, shared geo-data layers, and rendering the form.
   *
   * This method handles the following:
   * - Updates the geo-data selection in the geoDataStore if a layer with geo-data exists.
   * - If the current type is 'Point' and the selected layer matches, retrieves sibling layers and shared geo-data layers, then updates the layer selection in the layerStore.
   * - Clears the layer selection in the layerStore if the above conditions are not met.
   * - Triggers the rendering of the form.
   *
   * @return {void} No return value.
   */
  init(): void {
    const layerType = this.globalStore.selectedType();
    if (!layerType) return;

    if (this.selectedLayer && this.selectedLayer.geoData) {
      this.geoDataStore.singleSelection(this.selectedLayer.geoData);
    } else {
      this.geoDataStore.singleSelection('');
    }

    this.isReadonly = !this.permissionService.checkDataLayerRights(
      layerType._id,
      this.activeImport ? 'import' : 'edit',
      false
    );

    if (!this.isReadonly) {
      this.renderForm();
    }

    if (
      this.type === 'Point' &&
      this.selectedLayer &&
      this.selectedLayer.type === 'Point'
    ) {
      this.layerSiblings = this.layerStore.getSiblings(this.selectedLayer);
      if (this.selectedLayer.geoData) {
        this.sharedGeoDataLayers = this.layerStore.getLayersByGeoData(
          this.selectedLayer.geoData
        );
        this.layerStore.updateSelection([...this.sharedGeoDataLayers]);
      } else {
        this.layerStore.updateSelection([this.selectedLayer]);
      }
    } else {
      this.layerStore.clearSelection();
    }

    this.renderForm();
  }

  /**
   * Opens a context menu for a specific feature at a given index with associated event.
   *
   * @param {MouseEvent} event - The mouse event that triggered the menu opening.
   * @param {number} index - The index of the feature in the list.
   * @param {GeoJSON.Feature} feature - The feature to be interacted with.
   * @param {Menu} menu - The menu instance to be toggled.
   * @return {void} This method does not return a value.
   */
  openMenu(
    event: MouseEvent,
    index: number,
    feature: GeoJSON.Feature<GeoJSON.Point | GeoJSON.Polygon>,
    menu: Menu
  ): void {
    this.items = [
      {
        label: 'bearbeiten',
        icon: 'fas fa-pen',
        command: () => this.editFeature(feature, index),
      },
      {
        label: 'löschen',
        icon: 'fas fa-trash',
        command: () => this.removeFeature(index),
      },
    ];
    queueMicrotask(() => menu.toggle(event));
  }

  /**
   * Initializes and renders the feature list.
   *
   * This method resets the list, filters its features, and populates it
   * with either provided input features or features from the geo data store.
   *
   * @return {void} Does not return a value.
   */
  renderForm(): void {
    this.resetForm();
    if (this.activeImport) {
      this.currentFeatures = this.inputFeatures || [];
    } else {
      this.currentFeatures =
        this.inputFeatures || this.geoDataStore.featuresFromSelection();
    }
    this.filteredFeatures = [...this.currentFeatures];
    this.initialized = true;
    this.featureEdited = false;

    if (this.selectedLayer?.geoData) {
      this.groups =
        this.geoDataStore.getGroupsById(this.selectedLayer.geoData) || [];

      this.defaultGroup = this.geoDataStore.getDefaultGroupById(
        this.selectedLayer.geoData
      );
    } else {
      this.groups = [];
      this.defaultGroup = '';
    }

    this.validateFeatures();
  }

  /**
   * Retrieves the 'features' form array from the feature form.
   *
   * @return {FormArray} The form array that represents the 'features' control from the form.
   * @deprecated No longer using FormArray for the whole list.
   */
  get features(): any {
    return {
      controls: this.currentFeatures,
      errors: this.featureErrors,
    };
  }

  featureErrors: any = null;

  validateFeatures(): void {
    const errors: any = {};

    // Uniqueness check
    const keys = this.currentFeatures.map(f => f.properties?.['key']);
    const duplicates = keys.filter(
      (key, index, self) => self.indexOf(key) !== index
    );
    if (duplicates.length > 0) {
      errors['nonUniqueKeys'] = duplicates;
    }

    // Differences check
    const fixedKeys = this.geoDataStore.keysFromSelection();
    if (fixedKeys && fixedKeys.length > 0) {
      const missingKeys = fixedKeys.filter(key => !keys.includes(key));
      if (missingKeys.length > 0) {
        errors['differences'] = missingKeys;
      }
    }

    this.featureErrors = Object.keys(errors).length > 0 ? errors : null;
  }

  /**
   * Resets the form by clearing any stored features.
   *
   * @return {void} Does not return any value.
   */
  resetForm(): void {
    this.currentFeatures = [];
    this.filteredFeatures = [];
    this.featureErrors = null;
    this.featureEdited = false;
  }

  /**
   * Creates a FormGroup for a single feature.
   *
   * @param {GeoJSON.Feature<GeoJSON.Point | GeoJSON.Polygon>} [feature]
   * @return {FormGroup}
   */
  createFeatureGroup(
    feature?: GeoJSON.Feature<GeoJSON.Point | GeoJSON.Polygon>
  ): FormGroup {
    return this.fb.group({
      type: ['Feature'],
      properties: this.fb.group({
        key: [
          {
            value: feature?.properties?.['key'] || '',
            disabled: !!feature?.properties?.['key'] && !this.inputFeatures,
          },
          Validators.required,
        ],
        name: [feature?.properties?.['name'] || '', Validators.required],
        group: [feature?.properties?.['group'] || ''],
        address: [feature?.properties?.['address'] || ''],
        information: [feature?.properties?.['information'] || ''],
        contact: [feature?.properties?.['contact'] || ''],
        description: [feature?.properties?.['description'] || ''],
      }),
      geometry: this.fb.group({
        coordinates: [feature?.geometry?.coordinates || []],
        type: [feature?.geometry?.type || this.type],
      }),
    });
  }

  addFeature(feature?: GeoJSON.Feature<GeoJSON.Point | GeoJSON.Polygon>): void {
    // This is now handled differently, but we keep it for potential new feature addition
    if (feature) {
      this.currentFeatures.push(feature);
      this.renderForm();
    }
  }

  /**
   * Retrieves the feature name associated with the given key.
   *
   * @param {string} key - The unique identifier used to find the corresponding feature name.
   * @return {string | undefined} The name of the feature associated with the provided key, or undefined if no matching feature is found.
   */
  getFeatureNameByKey(key: string): string | undefined {
    return this.geoDataStore
      .featuresFromSelection()
      .find((feature: Feature) => {
        return feature.properties.key === key;
      })?.properties.name;
  }

  /**
   * Removes a feature from the features list at the specified index.
   *
   * @param {number} index - The zero-based index of the feature to be removed.
   * @return {void} This method does not return a value.
   */
  removeFeature(index: number): void {
    this.currentFeatures.splice(index, 1);
    this.renderForm();
  }

  /**
   * Submits the form data by generating geo-data based on the form's features
   * and performing either an update or addition operation depending on the
   * selected geo-data. Provides success messages upon completion and emits
   * a submitted event.
   *
   * @return {void} No return value.
   */
  submitForm(): void {
    const geoData = this.geoDataStore.generateByFeatures(
      this.currentFeatures,
      this.currentFeatures,
      this.defaultGroup
    );

    this.validateFeatures();

    if (
      !this.featureErrors ||
      this.type === 'Point' ||
      this.ignoreDifferentKeysValidator
    ) {
      geoData.poiJson = this.type === 'Point';
      const stateUpdates: any = {
        triggerOptions: {
          dataLayer: this.selectedLayer,
          singleLayerOnly: this.type === 'Polygon' && this.decoupleFromSiblings,
        },
      };
      if (this.type === 'Point' && this.overrideLayersByDifferences) {
        stateUpdates.triggerOptions.dataLayers = [
          ...this.layerStore.selection(),
        ];
      } else {
        delete stateUpdates.triggerOptions.dataLayers;
      }

      patchState(this.geoDataStore, stateUpdates);

      if (this.selectedGeoData) {
        geoData._id =
          (this.type === 'Point' && this.overrideLayersByDifferences) ||
          (this.type === 'Polygon' && this.decoupleFromSiblings)
            ? ''
            : this.selectedGeoData._id;
        geoData.message = {
          severity: 'success',
          summary: 'Änderungen erfolgreich!',
          detail: 'Standorte wurden aktualisiert.',
        };

        if (geoData._id) {
          this.geoDataStore.update(geoData);
        } else {
          this.geoDataStore.addItem(geoData);
        }
        this.editedIndices = new Set<number>();
        this.submitted.emit();
        this.renderForm();
      } else {
        geoData.message = {
          severity: 'success',
          summary: 'Import erfolgreich!',
          detail:
            this.type === 'Point'
              ? 'Standorte wurden erfolgreich hinzugefügt!'
              : 'Teilräume wurden erfolgreich hinzugefügt!',
        };
        this.geoDataStore.addItem(geoData);
        this.submitted.emit();
        //this.renderForm();
        this.initialized = false;
        this.featureEdited = false;
      }
    }
  }

  onSearch(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredFeatures = [...this.currentFeatures];
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();

      this.filteredFeatures = this.currentFeatures.filter(feature => {
        return Object.values(feature.properties || {}).some(value => {
          return value && String(value).toLowerCase().includes(lowerSearchTerm);
        });
      });
    }
  }

  /**
   * closes meta-form
   */
  closeMetaForm() {
    this.featureToEdit = undefined;
    this.featureToEditIndex = undefined;
  }

  /**
   * edit feature in dialog
   * @param feature
   * @param i
   */
  editFeature(
    feature: GeoJSON.Feature<GeoJSON.Point | GeoJSON.Polygon>,
    i: number
  ) {
    this.featureToEdit = this.createFeatureGroup(feature);
    this.featureToEditIndex = i;
  }

  /**
   * update feature from meta-form
   * @param form
   */
  updateFeature(form: FormGroup) {
    const rawValue = form.getRawValue();
    console.log('form', rawValue);
    if (this.featureToEditIndex !== undefined) {
      this.currentFeatures[this.featureToEditIndex] = {
        ...this.currentFeatures[this.featureToEditIndex],
        properties: {
          ...this.currentFeatures[this.featureToEditIndex].properties,
          ...rawValue.properties,
        },
      };

      const updatedSet = new Set(this.editedIndices);
      updatedSet.add(this.featureToEditIndex);
      this.editedIndices = updatedSet;
      this.featureEdited = true;

      console.log('editedIndices', this.editedIndices);
      this.filteredFeatures = [...this.currentFeatures];
      this.validateFeatures();

      this.closeMetaForm();
    }
  }

  onDefaultGroupChange(group: string) {
    this.defaultGroup = group;
    this.featureEdited = true;
  }

  /**
   * Checks the difference between the currently selected layers and the shared geo-data layers.
   * Updates the state indicating if there are overriding layers due to these differences.
   *
   * @param {DataLayer[]} newSelection - An array of data layers representing the new selection.
   * @return {void} This method does not return a value.
   */
  private checkSharedLayers(newSelection: DataLayer[]): void {
    if (!this.selectedLayer) return;
    const diffLayers = [
      ...newSelection.filter(
        layer => !this.sharedGeoDataLayers.includes(layer)
      ),
      ...this.sharedGeoDataLayers.filter(
        layer => !newSelection.includes(layer)
      ),
    ];

    this.overrideLayersByDifferences = diffLayers.length > 0;
  }

  /**
   * Constructs a descriptive header text based on the type and input features.
   *
   * @return {string} A string representing the header text. It indicates whether the object type is 'Point' or not,
   *                  and specifies if the information is sourced from an import or a data layer.
   */
  get headerText(): string {
    return `Übersicht der ${this.type === 'Point' ? 'Standorte' : 'Teilräume'}`;
    //return `Übersicht der ${this.type === 'Point' ? 'Standorte' : 'Teilräume'}${this.inputFeatures ? ' aus dem Import' : ' aus der Datenschicht'}`;
  }

  cancel(): void {
    this.inputFeatures = this.geoDataStore.featuresFromSelection();
    this.cancelled.emit();
  }

  ngOnDestroy() {
    this.cancel();
    this.featureForm.reset();
    this.layerStore.clearSelection();
  }
}
